import { QueryClient, QueryFunction, type Query } from "@tanstack/react-query";
import {
  classifyQueryTier,
  RUNTIME_CONFIG_DEFAULT_SETTINGS,
  type RuntimeConfigSettings,
  type RuntimeConfigTier,
} from "@shared/v2/runtime-config";

// ─── Module-level live config cache ──────────────────────────────────────
// Holds the most recently fetched runtime config. The QueryClient defaults
// (refetchInterval, refetchOnWindowFocus, etc.) read from this object on
// every query evaluation, so updating it via `applyRuntimeConfig` instantly
// changes behaviour for every existing observer without needing to recreate
// the QueryClient. Fallback is the Balanced preset baked into the bundle —
// used during the first ~50ms before /api/runtime-config has responded.
let currentConfig: RuntimeConfigSettings = RUNTIME_CONFIG_DEFAULT_SETTINGS;

/** Returns the current effective runtime config snapshot (read-only copy). */
export function getCurrentRuntimeConfig(): RuntimeConfigSettings {
  return currentConfig;
}

/**
 * Apply a new runtime config to the live QueryClient.
 *
 * Steps:
 *  1. Update the module-level cache so future query evaluations pick it up.
 *  2. Invalidate observers so their `staleTime` is reconsidered (they will
 *     re-evaluate `refetchInterval` on the next render tick — this is how
 *     React Query "changes gears" without recreating itself).
 *
 * Called by the App bootstrap effect after the first fetch and after the
 * IT-admin Settings tab saves a change.
 */
export function applyRuntimeConfig(next: RuntimeConfigSettings): void {
  currentConfig = next;
  // Force every observer to re-read defaults. We don't want to invalidate
  // their data (no need to refetch immediately just because the cadence
  // changed), so we touch the defaults key only — this nudges TanStack to
  // recompute interval/staleTime for each observer on the next render.
  queryClient.getQueryCache().getAll().forEach((q) => {
    q.setOptions({ ...q.options });
  });
}

function tierForQuery(query: Pick<Query, "queryKey">): RuntimeConfigTier {
  return classifyQueryTier(query.queryKey);
}

function refetchIntervalFor(query: Query): number | false {
  const tier = tierForQuery(query);
  const seconds = currentConfig.refreshIntervals[tier];
  return seconds > 0 ? seconds * 1000 : false;
}

function staleTimeFor(query: Query): number {
  const tier = tierForQuery(query);
  const seconds = currentConfig.refreshIntervals[tier];
  // When auto-refresh is off for a tier, treat data as fresh forever
  // (matches the legacy pre-feature behaviour). Otherwise, half the
  // interval is a sensible "fresh" window so a manual remount during
  // the window doesn't trigger an extra fetch.
  if (seconds <= 0) return Infinity;
  return Math.floor((seconds * 1000) / 2);
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)sdf\.csrf=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function getBrowserTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const headers: Record<string, string> = {};
  if (data) headers["Content-Type"] = "application/json";
  const csrf = getCsrfToken();
  if (csrf) headers["x-csrf-token"] = csrf;
  const tz = getBrowserTimezone();
  if (tz) headers["x-timezone"] = tz;

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const tz = getBrowserTimezone();
    const headers: Record<string, string> = {};
    if (tz) headers["x-timezone"] = tz;
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
      headers,
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      // Functions read from `currentConfig`, which `applyRuntimeConfig`
      // updates at runtime. This is the "change gears" mechanism — the IT
      // admin saves a setting, the bootstrap re-fetches /api/runtime-config,
      // and every existing observer picks up the new cadence on its next
      // tick without a page refresh on the affected user (Flavour A still
      // requires a page refresh on OTHER users since they don't know yet).
      refetchInterval: refetchIntervalFor,
      refetchIntervalInBackground: () => currentConfig.refetchInBackground,
      refetchOnWindowFocus: () => currentConfig.refetchOnFocus,
      refetchOnReconnect: () => currentConfig.refetchOnReconnect,
      staleTime: staleTimeFor,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
